import { useState, useEffect } from "react";
import { Button, Form, Header, Link, SpaceBetween } from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import FormInputNewCasestudyPanel from "./tab-form-input-new-casestudy-panel";
import { DataProvider } from "../../../api/data-provider";
import { useNotification } from "../../../components/Context/NotificationContext";
import { useNavigate, useBeforeUnload } from "react-router-dom";
import { useCasestudies, useUserProfile } from "../../../components";
import paletteConfig from "../../../config/aws-palette-config.json";
import { solutionNameDictionary, categoryDictionary, domainDictionary } from "../../../common/chinese-english-dictionary";
import {
  solutionNameOptions_en,
  solutionNameOptions_cn,
  categoryOptions_en,
  categoryOptions_cn,
  domainOptions_en,
  domainOptions_cn,
} from "../../../common/formOptions";
import { getFileExtension, generateRandomId } from "../../../common/utils";

const DEBUG = paletteConfig.debugMode;

/*****************************************************************************/
function generateTranslatedAttribute(attribute, value, dictionary) {
  const contentLanguage = attribute.endsWith("_en") ? "cn" : "en";
  const otherAttribute = attribute.slice(0, -2) + contentLanguage;

  const otherValue = value.map((v) => {
    const translatedValue = dictionary[v["label"]];
    if (!translatedValue) {
      DEBUG && console.error(`Invalid value in 'value' array: ${v}`);
    }
    return { label: translatedValue, value: translatedValue };
  });

  return {
    otherAttribute,
    otherValue,
  };
}

/*****************************************************************************/
function FormInputBaseFormContent({ content, errorText = null }) {
  const { t } = useTranslation();
  const { setCasestudiesFetched } = useCasestudies();
  const { pushNewNotification } = useNotification();
  const navigate = useNavigate();
  const { userLogin, contentLanguage, today } = useUserProfile();

  const [renderFileUploader, setRenderFileUploader] = useState(false);
  const [randomId, setRandomId] = useState("");
  const [fileLogo, setFileLogo] = useState([]);
  const [fileCasestudyImage, setFileCasestudyImage] = useState([]);
  const [fileArchitecture, setFileArchitecture] = useState([]);
  const [errorTextLogo, setErrorTextLogo] = useState("");
  const [errorTextCasestudyImage, setErrorTextCasestudyImage] = useState("");
  const [errorTextArchitecture, setErrorTextArchitecture] = useState("");
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [casestudyImageUploadProgress, setCasestudyImageUploadProgress] = useState(0);
  const [architectureUploadProgress, setArchitectureUploadProgress] = useState(0);

  const [submitting, setSubmitting] = useState(false); // 提交期间，（1）禁用button；（2）禁止离开页面；
  const [isLogoUploadCompleted, setIsLogoUploadCompleted] = useState(false);
  const [isCasestudyImageUploadCompleted, setIsCasestudyImageUploadCompleted] = useState(false);
  const [isArchitectureUploadCompleted, setIsArchitectureUploadCompleted] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(true); // 在上传前的判断逻辑中没有出错，已经在上传，但是不是只传输完成
  const [isCasestudyCreateCompleted, setIsCasestudyCreateCompleted] = useState(false);

  const location = useLocation();

  // It is an object of casestudy containing all attributes except 'createdBy' which will be automatically generated by the backend lambda before inserting the item into Dynamodb tables.
  const [data, setData] = useState({});
  const [formattedData, setFormattedData] = useState({
    publicReference: false,
  });

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/

  useEffect(() => {
    if (location.state && location.state.selectedSolution) {
      const selectedSolution = location.state.selectedSolution;
      DEBUG && console.log("%clocation.state.selectedSolution", "color: green; font-weight: bold;", selectedSolution);
      setData((prevState) => {
        return {
          ...prevState,
          solutionNameAbbr: {
            label: selectedSolution[`solutionName_${contentLanguage}`],
            value: selectedSolution.solutionNameAbbr,
          },
          solutionsUsed_en: [solutionNameOptions_en.find((item) => item.value === selectedSolution.solutionNameAbbr)],
          solutionsUsed_cn: [solutionNameOptions_cn.find((item) => item.value === selectedSolution.solutionNameAbbr)],
          category_en: selectedSolution.category_en.map((category) => {
            return categoryOptions_en.find((item) => item.value === category);
          }),
          category_cn: selectedSolution.category_cn.map((category) => {
            return categoryOptions_cn.find((item) => item.value === category);
          }),
          domain_en: selectedSolution.domain_en.map((category) => {
            return domainOptions_en.find((item) => item.value === category);
          }),
          domain_cn: selectedSolution.domain_cn.map((category) => {
            return domainOptions_cn.find((item) => item.value === category);
          }),
        };
      });
      setFormattedData((prevState) => {
        return {
          ...prevState,
          solutionNameAbbr: selectedSolution.solutionNameAbbr,
          solutionName_en: selectedSolution.solutionName_en, //不在屏幕上显示
          solutionName_cn: selectedSolution.solutionName_cn, //不在屏幕上显示
          solutionsUsed_en: [solutionNameOptions_en.find((item) => item.value === selectedSolution.solutionNameAbbr).label],
          solutionsUsed_cn: [solutionNameOptions_cn.find((item) => item.value === selectedSolution.solutionNameAbbr).label],
          category_en: [...selectedSolution.category_en],
          category_cn: [...selectedSolution.category_cn],
          domain_en: [...selectedSolution.domain_en],
          domain_cn: [...selectedSolution.domain_cn],
        };
      });
    }
  }, [location]);

  // 当用户没有输入Partition Key和Sort Key的时候，是不在页面上渲染上传图片的Formfields的。要保证图片信息写到dynamodb的时候，有Primary Key可以查询到对应的item。
  useEffect(() => {
    if (
      formattedData.solutionNameAbbr &&
      formattedData.customerNameAbbr &&
      formattedData.customerName_en &&
      formattedData.customerName_cn &&
      formattedData.year &&
      formattedData.headline_en &&
      formattedData.headline_cn
    ) {
      setRenderFileUploader(true);
    } else {
      if (renderFileUploader) {
        setRenderFileUploader(false);
      }
    }
    DEBUG && console.log("%crenderFileUploader", "color: blue; font-weight: bold;", renderFileUploader);
  }, [formattedData]);

  useEffect(() => {
    // For debug purpose
    DEBUG && console.log("%cformatedData (used to call API to create DynamoDB items):", "color: red; font-weight: bold;", formattedData);
    DEBUG && console.log("%cdata (used to call API to create DynamoDB items):", "color: red; font-weight: bold;", data);
  }, [formattedData]);

  useEffect(() => {
    setRandomId(generateRandomId());
  }, []);

  useEffect(() => {
    // For debug purpose
    DEBUG && console.log("%clogoUploadProgress", "color: blue; font-weight: bold;", logoUploadProgress);
    DEBUG && console.log("%ccasestudyImageUploadProgress", "color: blue; font-weight: bold;", casestudyImageUploadProgress);
    DEBUG && console.log("%carchitectureUploadProgress", "color: blue; font-weight: bold;", architectureUploadProgress);
  }, [logoUploadProgress, casestudyImageUploadProgress, architectureUploadProgress]);

  // 监控状态，一旦文件上传成功，并且上传完毕，就执行对DynamoDB的写操作，更新数据库。
  useEffect(() => {
    async function createCasestudy() {
      DEBUG && console.log("%cuploadSuccessful", "color: green; font-weight: bold;", uploadSuccessful);
      DEBUG && console.log("%cisLogoUploadCompleted", "color: green; font-weight: bold;", isLogoUploadCompleted);
      DEBUG && console.log("%cisCasestudyImageUploadCompleted", "color: green; font-weight: bold;", isCasestudyImageUploadCompleted);
      DEBUG && console.log("%cisArchitectureUploadCompleted", "color: green; font-weight: bold;", isArchitectureUploadCompleted);

      if (uploadSuccessful && isLogoUploadCompleted && isCasestudyImageUploadCompleted && isArchitectureUploadCompleted) {
        try {
          /* —————— 请求Api去写后端数据库之前，为每个新的casestudy生成一个唯一的id和其他几种重要属性。这些全部在后端生成，不在前端生成 —————— */
          // formattedData.id = uuidv4();
          // formattedData.isPublished = false;
          // formattedData.createdBy = userLogin; 由后端Api自动生成，基于Cognito Authorizer提供的userId，理论上来说应该userLogin === userId。
          // formattedData.createdDate 由后端Api自动生成
          /* ———————————————————————————————————————————————————————————————————————————————————————————————————————————————— */

          const response = await new DataProvider().createCasestudy(formattedData);
          DEBUG && console.log("%cApiCallCompleted, set 'isCasestudyCreateCompleted' to 'true' now.", "color: green; font-weight: bold;", response);
          setIsCasestudyCreateCompleted(true);
          const newMessage = {
            type: "success",
            content: t("notification.createCasestudy"),
            dismissible: true,
            dismissLabel: "Dismiss message",
            statusIconAriaLabel: "success",
          };
          pushNewNotification(newMessage);
        } catch (error) {
          console.error("Create Casestudy failed:", error);
          const newMessage = {
            type: "error",
            content: t("notification.createOrUpdateCasestudyApiError"),
            dismissible: true,
            dismissLabel: "Dismiss message",
            statusIconAriaLabel: "error",
          };
          pushNewNotification(newMessage);
        } finally {
          setSubmitting(false);
        }
      }
    }

    createCasestudy();
  }, [uploadSuccessful, isLogoUploadCompleted, isCasestudyImageUploadCompleted, isArchitectureUploadCompleted, formattedData]);

  // 监听上传状态，如果已经完成api call，创建了dynamodb的新条目，就立刻发出通知，并重新加载最新的case study list
  useEffect(() => {
    if (isCasestudyCreateCompleted) {
      setCasestudiesFetched(false);
      navigate("/casestudies");
    }
  }, [isCasestudyCreateCompleted]);

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onChangeCasestudyHandler = (attribute, value) => {
    DEBUG && console.log(`%c${attribute}:`, "color: blue; font-weight: bold;", value);

    switch (attribute) {
      case "solutionNameAbbr":
        // 当solutionNameAbbr被赋值的时候，使用代码自动生成solutionName_en和solutionName_cn的值
        const solutionName_en_Value = solutionNameOptions_en.find((item) => item.value === value.value).label;
        const solutionName_cn_Value = solutionNameOptions_cn.find((item) => item.value === value.value).label;

        setData({ ...data, [attribute]: value });
        setFormattedData({ ...formattedData, [attribute]: value.value, solutionName_en: solutionName_en_Value, solutionName_cn: solutionName_cn_Value });
        break;

      case `solutionsUsed_${contentLanguage}`:
        const otherLanguage = contentLanguage === "en" ? "cn" : "en";
        const otherSolutionsUsedAttribute = `solutionsUsed_${otherLanguage}`;
        const otherSolutionsUsedValue = value.map((item) => ({
          label: solutionNameDictionary[item.label],
          value: item.value,
        }));

        const transformedValue = value.map((item) => item.label); // Transform the element from {} to String
        DEBUG && console.log("%csolutionNameDictionary", "color: blue; font-weight: bold;", solutionNameDictionary);
        const otherTransformedValue = transformedValue.map((item) => solutionNameDictionary[item]);
        DEBUG && console.log("%cotherTransformedValue", "color: blue; font-weight: bold;", otherTransformedValue);

        setData({ ...data, [attribute]: value, [otherSolutionsUsedAttribute]: otherSolutionsUsedValue });
        setFormattedData({ ...formattedData, [attribute]: transformedValue, [otherSolutionsUsedAttribute]: otherTransformedValue });
        break;

      case `category_${contentLanguage}`: // 自动生成另一种语言的Key/Value Pair
        const { otherAttribute: otherCategoryAttribute, otherValue: otherCategoryValue } = generateTranslatedAttribute(attribute, value, categoryDictionary);

        DEBUG && console.log("%cotherCategoryAttribute", "color: blue; font-weight: bold;", otherCategoryAttribute);
        DEBUG && console.log("%cotherCategoryValue", "color: blue; font-weight: bold;", otherCategoryValue);

        setData({ ...data, [attribute]: value, [otherCategoryAttribute]: otherCategoryValue });
        setFormattedData({
          ...formattedData,
          [attribute]: value.map((item) => item.value),
          [otherCategoryAttribute]: otherCategoryValue.map((item) => item.value),
        });
        break;

      case `domain_${contentLanguage}`:
        const { otherAttribute: otherDomainAttribute, otherValue: otherDomainValue } = generateTranslatedAttribute(attribute, value, domainDictionary);

        setData({ ...data, [attribute]: value, [otherDomainAttribute]: otherDomainValue });
        setFormattedData({
          ...formattedData,
          [attribute]: value.map((item) => item.value),
          [otherDomainAttribute]: otherDomainValue.map((item) => item.value),
        });
        break;

      case "year":
        setData({ ...data, [attribute]: value });
        setFormattedData({ ...formattedData, [attribute]: value.value });
        break;

      // case "publicReference":
      //   const convertedValue = !!value ? "Yes" : "No";
      //   setData({ ...data, [attribute]: value });
      //   setFormattedData({ ...formattedData, [attribute]: convertedValue });
      //   break;

      default:
        setFormattedData({ ...formattedData, [attribute]: value });
        break;
    }
  };

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onChangeFileHandler = (attribute, value) => {
    switch (attribute) {
      case "customerLogo":
        setFileLogo(value);
        break;

      case "casestudyImage":
        setFileCasestudyImage(value);
        break;

      case "techArchitectureImage":
        setFileArchitecture(value);
        break;

      default:
        break;
    }
  };

  const onChangeFileErrorHandler = (fileName, value) => {
    switch (fileName) {
      case "customerLogo":
        setErrorTextLogo(value);
        break;

      case "casestudyImage":
        setErrorTextCasestudyImage(value);
        break;

      case "techArchitectureImage":
        setErrorTextArchitecture(value);
        break;

      default:
        break;
    }
  };

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  async function handleFileUploads() {
    DEBUG && console.log("%cformatedData", "color: blue; font-weight: bold;", formattedData);

    const filePath = `store/casestudies/${formattedData.solutionNameAbbr}-${formattedData.customerNameAbbr}`;
    const newFormatedData = { ...formattedData };

    const updateNewFormatedData = (key, value) => {
      newFormatedData[key] = value;
    };

    const logoUploadPromise = fileLogo[0]
      ? (async () => {
          try {
            const ext = getFileExtension(fileLogo[0].name);
            const newFileName = `customer-logo-${today}-${randomId}.${ext}`;
            updateNewFormatedData("customerLogo", `${filePath}/${newFileName}`); // 不需要以'/'作为首字符，统一按照S3 Key的命名规则。我在渲染的时候会自动加上'/'，记住渲染的时候一定要加上，否则无法正常渲染
            await new DataProvider().uploadToS3(userLogin, fileLogo[0], filePath, setLogoUploadProgress, setIsLogoUploadCompleted, newFileName);
          } catch (error) {
            setUploadSuccessful(false);
            onChangeFileErrorHandler("customerLogo", t(`casestudies:CasestudyCreateForm.anErrorOccured`));
            console.error("Logo upload failed:", error);
          }
        })()
      : (async () => {
          DEBUG && console.log("%ccustomerLogo image file doesn't exist, set 'isLogoUploadCompleted' to 'true'.", "color: green; font-weight: bold;");
          setIsLogoUploadCompleted(true);
        })();

    const casestudyImageUploadPromise = fileCasestudyImage[0]
      ? (async () => {
          try {
            const ext = getFileExtension(fileCasestudyImage[0].name);
            const newFileName = `casestudy-image-${today}-${randomId}.${ext}`;
            updateNewFormatedData("casestudyImage", `${filePath}/${newFileName}`);
            await new DataProvider().uploadToS3(
              userLogin,
              fileCasestudyImage[0],
              filePath,
              setCasestudyImageUploadProgress,
              setIsCasestudyImageUploadCompleted,
              newFileName
            );
          } catch (error) {
            setUploadSuccessful(false);
            onChangeFileErrorHandler("casestudyImage", t(`casestudies:CasestudyCreateForm.anErrorOccured`));
            console.error("casestudyImage upload failed:", error);
          }
        })()
      : (async () => {
          DEBUG && console.log("%ccasestudy image file doesn't exist, set 'isCasestudyImageUploadCompleted' to 'true'.", "color: green; font-weight: bold;");
          setIsCasestudyImageUploadCompleted(true);
        })();

    const architectureUploadPromise = fileArchitecture[0]
      ? (async () => {
          try {
            const ext = getFileExtension(fileArchitecture[0].name);
            const newFileName = `tech-architecture-${today}-${randomId}.${ext}`;
            updateNewFormatedData("techArchitectureImage", `${filePath}/${newFileName}`);
            await new DataProvider().uploadToS3(
              userLogin,
              fileArchitecture[0],
              filePath,
              setArchitectureUploadProgress,
              setIsArchitectureUploadCompleted,
              newFileName
            );
          } catch (error) {
            setUploadSuccessful(false);
            onChangeFileErrorHandler("techArchitectureImage", t(`casestudies:CasestudyCreateForm.anErrorOccured`));
            console.error("techArchitecture image file upload failed:", error);
          }
        })()
      : (async () => {
          DEBUG &&
            console.log("%ctechArchitecture ImageFile doesn't exist, set 'isArchitectureUploadCompleted' to 'true'.", "color: green; font-weight: bold;");
          setIsArchitectureUploadCompleted(true);
        })();

    await Promise.all([logoUploadPromise, casestudyImageUploadPromise, architectureUploadPromise]);
    setFormattedData(newFormatedData);
  }

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onSubmitHandler = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      handleFileUploads(); // 调用新的handleFileUploads函数处理文件上传
    } catch (error) {
      console.error("One or more files failed to upload. New case study hasn't been put into DynamoDB. Try again on the webpage.");
      const newMessage = {
        type: "error",
        content: t("notification.filesUploadFailed"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "error",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
    }
  };

  const onCancelClick = () => {
    navigate("/casestudies");
  };

  // 点击按钮提交期间，阻止刷新和关闭
  useBeforeUnload((event) => {
    if (submitting) {
      event.preventDefault();
      event.returnValue = t("useBeforeUnload");
    }
  });

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  return (
    <form>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancelClick}>
              {t("casestudies:CasestudyCreateForm.cancelButton")}
            </Button>
            <Button variant="primary" onClick={onSubmitHandler} loading={submitting} disabled={!renderFileUploader || submitting}>
              {submitting ? t("casestudies:CasestudyCreateForm.createCasestudyButtonSubmitting") : t("casestudies:CasestudyCreateForm.createCasestudyButton")}
            </Button>
          </SpaceBetween>
        }
        errorText={errorText}
        errorIconAriaLabel="Error"
      >
        {content({
          onChangeCasestudyHandler,
          data,
          formattedData,
          setFormattedData,
          renderFileUploader,
          onChangeFileHandler,
          onChangeFileErrorHandler,
          fileLogo,
          fileCasestudyImage,
          fileArchitecture,
          errorTextLogo,
          errorTextCasestudyImage,
          errorTextArchitecture,
          logoUploadProgress,
          casestudyImageUploadProgress,
          architectureUploadProgress,
        })}
      </Form>
    </form>
  );
}

/*****************************************************************************/
export function FormInputFormContent({ loadHelpPanelContent }) {
  return (
    <FormInputBaseFormContent
      content={({
        onChangeCasestudyHandler,
        data,
        formattedData,
        setFormattedData,
        renderFileUploader,
        onChangeFileHandler,
        onChangeFileErrorHandler,
        fileLogo,
        fileCasestudyImage,
        fileArchitecture,
        errorTextLogo,
        errorTextCasestudyImage,
        errorTextArchitecture,
        logoUploadProgress,
        casestudyImageUploadProgress,
        architectureUploadProgress,
      }) => (
        <SpaceBetween size="l">
          <FormInputNewCasestudyPanel
            loadHelpPanelContent={loadHelpPanelContent}
            onChangeCasestudyHandler={onChangeCasestudyHandler}
            data={data}
            formattedData={formattedData}
            setFormattedData={setFormattedData}
            renderFileUploader={renderFileUploader}
            onChangeFileHandler={onChangeFileHandler}
            onChangeFileErrorHandler={onChangeFileErrorHandler}
            fileLogo={fileLogo}
            fileCasestudyImage={fileCasestudyImage}
            fileArchitecture={fileArchitecture}
            errorTextLogo={errorTextLogo}
            errorTextCasestudyImage={errorTextCasestudyImage}
            errorTextArchitecture={errorTextArchitecture}
            logoUploadProgress={logoUploadProgress}
            casestudyImageUploadProgress={casestudyImageUploadProgress}
            architectureUploadProgress={architectureUploadProgress}
          />
        </SpaceBetween>
      )}
    />
  );
}
